import type { getFormatter } from '../../utilities';
import type { TimelineFoldedCompany, TimelineTrack } from './timeline-layout';
import styles from './timeline-tooltip.module.css';

const FOLDED_LIST_LIMIT = 6;

const formatDate = (time: number): string =>
  new Date(time).toLocaleDateString('default', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

const yearOf = (time: number): number => new Date(time).getUTCFullYear();

const tenure = (company: TimelineFoldedCompany): string =>
  company.leftTime === null
    ? String(yearOf(company.joinTime))
    : `${yearOf(company.joinTime)}–${yearOf(company.leftTime)}`;

const FoldedList = ({
  heading,
  companies,
}: {
  heading: string;
  companies: TimelineFoldedCompany[];
}) => {
  const shown = companies
    .slice(0, FOLDED_LIST_LIMIT)
    .sort((a, b) => a.joinTime - b.joinTime);
  const more = companies.length - shown.length;
  return (
    <div
      className={styles.tooltipSection}
      data-testid="timeline-chart__tooltip-folded"
    >
      <div className={styles.tooltipHeading}>{heading}</div>
      <ul className={styles.tooltipList}>
        {shown.map((company) => (
          <li key={company.id} className={styles.tooltipListRow}>
            <span className={styles.tooltipListYear}>{tenure(company)}</span>
            <span className={styles.tooltipListName}>{company.label}</span>
          </li>
        ))}
      </ul>
      {more > 0 && <div className={styles.tooltipMore}>{`+${more} more`}</div>}
    </div>
  );
};

const bySignificance = (companies: TimelineFoldedCompany[]) =>
  [...companies].sort((a, b) => b.value - a.value || a.joinTime - b.joinTime);

const stationHeading = (companies: TimelineFoldedCompany[]): string => {
  const times = companies.map((company) => company.joinTime);
  const first = formatDate(Math.min(...times));
  const last = formatDate(Math.max(...times));
  return first === last ? `Joined ${first}` : `Joined ${first} – ${last}`;
};

const subtitleOf = (
  track: TimelineTrack,
  departed: boolean,
  exitNote: string | null
): string => {
  const parts: string[] = [];
  if (track.joinTime !== null) {
    parts.push(
      track.targetLabel === null
        ? `Joined ${formatDate(track.joinTime)}`
        : `Joined ${track.targetLabel} in ${formatDate(track.joinTime)}`
    );
  }
  if (departed) {
    const verb = track.exitKind === 'dissolved' ? 'dissolved' : 'left';
    const left = `${verb} ${formatDate(track.endTime)}`;
    parts.push(exitNote === null ? left : `${left} (${exitNote})`);
  }
  return parts.join(', ');
};

export const TimelineTooltipContent = ({
  track,
  domainEnd,
  format,
  station,
  exitNote = null,
}: {
  track: TimelineTrack;
  domainEnd: number;
  format: ReturnType<typeof getFormatter>;
  station: TimelineFoldedCompany[] | null;
  /** How it left, when the chart has no room to say it. */
  exitNote?: string | null;
}) => {
  const departed = track.endTime < domainEnd;
  const carriesOthers = track.value > track.ownValue;
  if (station !== null && station.length > 0) {
    return (
      <div
        className={styles.tooltipBlock}
        data-testid="timeline-chart__tooltip"
      >
        <div className={styles.tooltipHeader}>
          <div className={styles.tooltipTitle}>{track.label}</div>
          <div className={styles.tooltipSubtitle}>
            {`${station.length} folded in here`}
          </div>
        </div>
        <FoldedList
          heading={stationHeading(station)}
          companies={bySignificance(station)}
        />
      </div>
    );
  }
  const subtitle = subtitleOf(track, departed, exitNote);
  const showsParent =
    track.kind === 'track' &&
    track.parentLabel !== null &&
    track.parentLabel !== track.targetLabel;
  return (
    <div className={styles.tooltipBlock} data-testid="timeline-chart__tooltip">
      <div className={styles.tooltipHeader}>
        <div className={styles.tooltipTitle}>{track.label}</div>
        {subtitle && <div className={styles.tooltipSubtitle}>{subtitle}</div>}
      </div>
      {(!departed || showsParent) && (
        <dl className={styles.tooltipMetrics}>
          {!departed && (
            <>
              <dt>Employees</dt>
              <dd>{format(track.value)}</dd>
            </>
          )}
          {!departed && carriesOthers && (
            <>
              <dt>Own staff</dt>
              <dd>{format(track.ownValue)}</dd>
            </>
          )}
          {!departed && track.share > 0 && (
            <>
              <dt>Share of group</dt>
              <dd>{`${(track.share * 100).toFixed(1)}%`}</dd>
            </>
          )}
          {showsParent && (
            <>
              <dt>Parent</dt>
              <dd>{track.parentLabel}</dd>
            </>
          )}
        </dl>
      )}
      {track.folded.length > 0 && (
        <FoldedList
          heading={`Folded in · ${format(track.folded.length)}`}
          companies={bySignificance(track.folded)}
        />
      )}
    </div>
  );
};
